import {
    bulkCreateTasks,
    createMfhTasksFromTrigger,
    createTask,
    deleteTask,
    fetchTask,
    fetchTasks,
    fetchUpcomingTasks,
    syncTaskMilestone,
    updateManyTasks,
    updateTask,
    updateTaskStatus,
} from '@/api/basecamp.api';
import { getMfhTaskHierarchy, getMfhTaskMilestoneReport, getMfhTasks } from '@/api/mfh';
import type { MaybeRef } from '@/shared/typings';
import type {
    IFetchTaskQuery,
    IFetchUpcomingTasksQuery,
    ITask,
    IUpdateTaskStatusParams,
    TaskCreateParams,
    TaskExpandOpts,
    TaskTrigger,
    TaskUpdateParams,
} from '@condo/domain';
import { TaskStatus, TaskStatusTranslation, TaskCompletedStatuses } from '@condo/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { useModal, NTag, NButton, NDropdown, useDialog } from 'naive-ui';
import { computed, h, ref, unref, type VNode } from 'vue';
import { useUser } from './contact.hooks';
import { useUsers } from './user.hooks';
import TaskStatusModal from '@/components/tasks/TaskStatusModal.vue';
import TaskDateEditModal from '@/components/tasks/TaskDateEditModal.vue';
import { MessageType, showMessage } from '@/shared/messages';
import { taskStatusColors } from '@/components/tasks/tasks.helper';
import { enumToHuman, userName, confirmDelete } from '@/shared';
import dayjs from 'dayjs';

export const getTaskStatusColor = (status?: TaskStatus) => {
    if (!status) return 'default';
    return taskStatusColors[status] || 'default';
};

export const useTasks = (params: MaybeRef<IFetchTaskQuery>, expand: TaskExpandOpts, enabled: MaybeRef<boolean> = true) => {
    return useQuery(['tasks', params, expand], async () => fetchTasks({ query: unref(params), expand }), {
        initialData: {
            tasks: [],
            openCount: 0,
            count: 0,
        },
        enabled,
    });
};

export const useMfhTasks = (mfhId: MaybeRef<number>, expand: TaskExpandOpts = {}) => {
    const enabled = computed(() => !!unref(mfhId));
    return useQuery(['tasks', 'mfh', mfhId, expand], async () => getMfhTasks(unref(mfhId), expand), {
        initialData: {
            tasks: [],
            openCount: 0,
            count: 0,
        },
        enabled,
    });
};

export const useUpcomingTasks = (params: MaybeRef<IFetchUpcomingTasksQuery>, enabled: MaybeRef<boolean> = true) => {
    return useQuery(['tasks', 'upcoming', params], async () => fetchUpcomingTasks(unref(params)), {
        initialData: {
            mfhs: [],
            count: 0,
        },
        enabled,
    });
};

export const useMfhTaskMilestoneReport = (mfhId: MaybeRef<number>) => {
    const enabled = computed(() => !!unref(mfhId));
    return useQuery(['mfh-task-milestone-report', mfhId], async () => getMfhTaskMilestoneReport(unref(mfhId)), {
        initialData: [],
        enabled,
    });
};

export const useMfhTaskHierarchy = (mfhId: MaybeRef<number>) => {
    const enabled = computed(() => !!unref(mfhId));
    return useQuery(['tasks', 'mfh-hierarchy', mfhId], async () => getMfhTaskHierarchy(unref(mfhId)), {
        enabled,
    });
};

interface CreateMfhTasksFromTriggerMutationParams {
    mfhId: number;
    trigger?: TaskTrigger;
}

export const useTaskMutations = () => {
    const queryClient = useQueryClient();

    const refetch = () => {
        queryClient.invalidateQueries(['tasks']);
    };

    const { mutateAsync: createTaskMutation } = useMutation(async (task: TaskCreateParams) => createTask(task), {
        onSuccess: () => {
            refetch();
        },
    });

    const { mutateAsync: createMfhTasksFromTriggerMutation, isLoading: createMfhTasksFromTriggerMutationInProgress } = useMutation(
        async (params: CreateMfhTasksFromTriggerMutationParams) => createMfhTasksFromTrigger(params.mfhId, { trigger: params.trigger }),
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries(['tasks']);
            },
        },
    );

    const { mutateAsync: updateTaskMutation } = useMutation(async (task: TaskUpdateParams) => updateTask(task), {
        onSuccess: () => {
            refetch();
        },
    });

    const { mutateAsync: updateTaskStatusMutation, isLoading: updateTaskStatusMutationInProgress } = useMutation(
        async (payload: IUpdateTaskStatusParams) => {
            return updateTaskStatus(payload);
        },
        {
            onSuccess: () => {
                refetch();
            },
        },
    );

    const { mutateAsync: deleteTaskMutation } = useMutation(async (taskId: number) => deleteTask(taskId), {
        onSuccess: () => {
            refetch();
        },
    });

    const { mutateAsync: bulkCreateTasksMutations } = useMutation(
        async (payload: {
            tasks: Partial<ITask>[];
            entityType: 'estate' | 'mfh' | 'mfhPortfolio' | 'mfhBuilding';
            entityId: number;
            deduplicateSameTitle?: boolean;
        }) => bulkCreateTasks(payload),
        {
            onSuccess() {
                refetch();
            },
        },
    );

    const { mutateAsync: syncTaskMilestoneMutation, isLoading: syncTaskMilestoneMutationInProgress } = useMutation(
        async (taskId: number) => syncTaskMilestone(taskId),
        {
            onSuccess: () => {
                refetch();
            },
        },
    );

    const { mutateAsync: updateManyTasksMutation, isLoading: updateManyTasksMutationInProgress } = useMutation(
        async (tasks: TaskUpdateParams[]) => updateManyTasks(tasks),
        {
            onSuccess: () => {
                refetch();
            },
        },
    );

    return {
        refetch,
        updateTaskStatusMutationInProgress,
        syncTaskMilestoneMutationInProgress,
        updateManyTasksMutationInProgress,
        bulkCreateTasksMutations,
        createMfhTasksFromTriggerMutation,
        createMfhTasksFromTriggerMutationInProgress,
        createTaskMutation,
        updateTaskMutation,
        updateManyTasksMutation,
        updateTaskStatusMutation,
        deleteTaskMutation,
        syncTaskMilestoneMutation,
    };
};

export const useInlineTaskEdit = () => {
    const { updateTaskMutation } = useTaskMutations();
    const { activeUsers } = useUsers();

    const userOptions = computed(() => {
        return (
            Object.values(activeUsers.value)?.map(user => ({
                label: user.displayName || '',
                value: user.uid,
            })) ?? []
        );
    });

    const formatDateForAPI = (date: Date | null): Date | null => {
        if (!date) return null;
        return dayjs(date).endOf('day').toDate();
    };

    const updateTaskField = async (task: Partial<ITask>, field: string, value: string | Date | null) => {
        if (!task.taskId) return;

        if (field === 'expectedOrActualDate') {
            const isCompletedStatus = task.status && TaskCompletedStatuses.includes(task.status);
            if (isCompletedStatus) {
                await updateTaskMutation({
                    taskId: task.taskId,
                    statusUpdatedAt: {
                        ...task.statusUpdatedAt,
                        [TaskStatus.Completed]: formatDateForAPI(value as Date | null),
                    },
                });
            } else {
                await updateTaskMutation({
                    taskId: task.taskId,
                    expectedDate: formatDateForAPI(value as Date | null),
                });
            }
        } else if (field === 'startDate') {
            await updateTaskMutation({
                taskId: task.taskId,
                statusUpdatedAt: {
                    ...task.statusUpdatedAt,
                    [TaskStatus.InProgress]: formatDateForAPI(value as Date | null),
                },
            });
        } else if (field === 'dueDate') {
            await updateTaskMutation({
                taskId: task.taskId,
                [field]: formatDateForAPI(value as Date | null),
            });
        } else {
            await updateTaskMutation({
                taskId: task.taskId,
                [field]: value,
            });
        }
    };

    return {
        updateTaskField,
        userOptions,
    };
};

export const useTaskActions = () => {
    const modal = useModal();
    const dialog = useDialog();
    const { updateTaskMutation, updateTaskStatusMutation, deleteTaskMutation } = useTaskMutations();
    const { data: userData } = useUser();

    const editDrawerVisible = ref(false);
    const detailDrawerVisible = ref(false);
    const selectedTask = ref<Partial<ITask> | null>(null);

    const openEditDrawer = async (task: Partial<ITask> | null) => {
        if (!task?.taskId) return;
        const fullTask = await fetchTask(task.taskId, { select: ['*'] });
        selectedTask.value = fullTask;
        editDrawerVisible.value = true;
    };

    const closeEditDrawer = () => {
        editDrawerVisible.value = false;
        selectedTask.value = null;
    };

    const handleSaveTask = async () => {
        closeEditDrawer();
    };

    const openDetailDrawer = async (task: ITask) => {
        if (!task.taskId) return;
        const fullTask = await fetchTask(task.taskId, { select: ['*'] });
        selectedTask.value = fullTask;
        detailDrawerVisible.value = true;
    };

    const closeDetailDrawer = () => {
        detailDrawerVisible.value = false;
        selectedTask.value = null;
    };

    const handleEditFromDetail = async () => {
        if (!selectedTask.value) return;
        const task = selectedTask.value;
        closeDetailDrawer();
        await openEditDrawer(task as Partial<ITask>);
    };

    const openStatusModalFromDetail = async () => {
        if (!selectedTask.value) return;
        await openStatusModal(selectedTask.value as Partial<ITask>);
    };

    const openStatusModal = async (task: Partial<ITask>, onSave?: (data: { status?: TaskStatus; statusUpdatedAt?: Date }) => Promise<void>) => {
        if (!task.taskId) return;
        const fullTask = await fetchTask(task.taskId, { select: ['*'] });

        const modalInstance = modal.create({
            style: { width: '600px' },
            render: () =>
                h(TaskStatusModal, {
                    task: fullTask,
                    onClose: () => modalInstance.destroy(),
                    onSave: async (data: { status?: TaskStatus; statusUpdatedAt?: Date }) => {
                        const taskId = task.taskId;
                        if (!data?.status || !taskId) {
                            return;
                        }

                        await updateTaskStatusMutation({
                            taskId: taskId,
                            status: data.status,
                            statusUpdatedAt: data.statusUpdatedAt,
                        });

                        if (onSave) {
                            await onSave(data);
                        }

                        if (selectedTask.value && taskId === selectedTask.value.taskId) {
                            selectedTask.value = await fetchTask(taskId, { select: ['*'] });
                        }

                        modalInstance.destroy();
                    },
                }),
        });
    };

    const openDateEditModal = async (task: Partial<ITask>, onSave?: () => Promise<void>) => {
        if (!task.taskId) return;
        const fullTask = await fetchTask(task.taskId, { select: ['*'] });

        const modalInstance = modal.create({
            style: { width: '700px' },
            render: () =>
                h(TaskDateEditModal, {
                    task: fullTask,
                    onClose: () => modalInstance.destroy(),
                    onSave: async (data: { startDate?: Date; expectedDate?: Date; dueDate?: Date; completionDate?: Date }) => {
                        const taskId = task.taskId;
                        if (!taskId) {
                            return;
                        }

                        await updateTaskMutation({
                            taskId: taskId,
                            statusUpdatedAt: {
                                ...fullTask.statusUpdatedAt,
                                [TaskStatus.InProgress]: data.startDate,
                                [TaskStatus.Completed]: data.completionDate,
                            },
                            expectedDate: data.expectedDate,
                            dueDate: data.dueDate,
                        });

                        if (onSave) {
                            await onSave();
                        }

                        if (selectedTask.value && taskId === selectedTask.value.taskId) {
                            selectedTask.value = await fetchTask(taskId, { select: ['*'] });
                        }

                        modalInstance.destroy();
                    },
                }),
        });
    };

    const assignTaskToMe = async (task: ITask) => {
        if (!task.taskId) return;
        const user = userData.value?.user;
        if (!user) {
            showMessage('User not found', MessageType.Danger);
            return;
        }

        await updateTaskMutation({
            taskId: task.taskId,
            assignedTo: user?.uid,
        });
    };

    const deleteTask = async (
        task: Partial<ITask>,
        options?: {
            onSuccess?: () => Promise<void> | void;
            closeDrawer?: boolean;
        },
    ) => {
        const taskId = task.taskId;
        if (!taskId) return;

        const taskIdentifier = task.customId || `Task #${taskId}`;
        const taskText = task.taskText || '';

        const confirmationMessage = `Delete Task <span class="font-bold text-black bg-gray-100 px-1.5 py-0.5 rounded font-mono">${taskIdentifier}</span>${taskText ? ` <span class="font-bold">"${taskText}"</span>` : ''}? <span class="text-red-500 font-medium">Action can't be undone!</span>`;

        confirmDelete(dialog, confirmationMessage, async () => {
            await deleteTaskMutation(taskId);
            if (options?.closeDrawer && detailDrawerVisible.value) {
                closeDetailDrawer();
            }
            if (options?.onSuccess) {
                await options.onSuccess();
            }
        });
    };

    const taskStatusTag = (status: TaskStatus) => {
        return h('div', { style: { display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' } }, [
            h(
                NTag,
                {
                    size: 'tiny',
                    type: getTaskStatusColor(status),
                    bordered: false,
                    style: { minWidth: '110px', textAlign: 'center' },
                },
                {
                    default: () => (status ? TaskStatusTranslation[status as TaskStatus] || enumToHuman(status) : null),
                },
            ),
            h(
                NButton,
                {
                    size: 'tiny',
                    quaternary: true,
                    style: { padding: '2px 4px', visibility: 'hidden', pointerEvents: 'none' },
                },
                {
                    icon: () => h('i', { class: 'fa-solid fa-ellipsis-vertical', style: { fontSize: '12px' } }),
                },
            ),
        ]);
    };

    const taskActionsMenu = (
        task: ITask,
        options?: {
            showEdit?: boolean;
            onStatusSave?: (data: { status?: TaskStatus; statusUpdatedAt?: Date }) => Promise<void>;
            onAssignSuccess?: () => Promise<void>;
        },
    ): VNode => {
        const { showEdit = false, onStatusSave, onAssignSuccess } = options || {};

        const isAssignedToMe = userData.value?.user?.uid === task.assignedTo;
        const assigneeName = task.assignedTo ? userName(task.assignedTo, false, true, true) : 'Unassigned';

        const dropdownOptions = [
            {
                type: 'render',
                key: 'assignee-header',
                render: () =>
                    h('div', { style: { padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#666' } }, [
                        h('i', { class: 'fa-solid fa-user-circle', style: { fontSize: '14px' } }),
                        h('span', { style: { fontWeight: '500' } }, assigneeName),
                    ]),
            },
            ...(task.dueDate
                ? [
                      {
                          type: 'render',
                          key: 'due-date-header',
                          render: () =>
                              h(
                                  'div',
                                  {
                                      style: {
                                          padding: '4px 12px 8px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '8px',
                                          fontSize: '12px',
                                          color: '#666',
                                      },
                                  },
                                  [h('i', { class: 'fa-solid fa-calendar', style: { fontSize: '14px' } }), h('span', dayjs(task.dueDate).format('DD.MM.YYYY'))],
                              ),
                      },
                  ]
                : []),
            {
                type: 'divider',
                key: 'divider1',
            },
            {
                label: 'View Details',
                key: 'view',
                icon: () => h('i', { class: 'fa-light fa-eye' }),
            },
            {
                label: 'Update Status',
                key: 'status',
                icon: () => h('i', { class: 'fa-solid fa-play' }),
            },
            {
                label: 'Update Dates',
                key: 'dates',
                icon: () => h('i', { class: 'fa-regular fa-calendar' }),
            },
            ...(showEdit
                ? [
                      {
                          label: 'Edit Task',
                          key: 'edit',
                          icon: () => h('i', { class: 'fa-light fa-pen-to-square' }),
                      },
                  ]
                : []),
            ...(isAssignedToMe
                ? []
                : [
                      {
                          label: 'Assign to me',
                          key: 'assign',
                          icon: () => h('i', { class: 'fa-regular fa-user-pen' }),
                      },
                  ]),
        ];

        const handleDropdownSelect = async (key: string) => {
            switch (key) {
                case 'view':
                    openDetailDrawer(task);
                    break;
                case 'status':
                    openStatusModal(task, onStatusSave);
                    break;
                case 'dates':
                    await openDateEditModal(task);
                    break;
                case 'edit':
                    openEditDrawer(task);
                    break;
                case 'assign':
                    await assignTaskToMe(task);
                    if (onAssignSuccess) {
                        await onAssignSuccess();
                    }
                    break;
            }
        };

        return h('div', { style: { display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' } }, [
            h(
                NTag,
                {
                    size: 'tiny',
                    type: getTaskStatusColor(task.status),
                    bordered: false,
                    style: { cursor: 'pointer', minWidth: '110px', textAlign: 'center' },
                    onClick: () => openStatusModal(task, onStatusSave),
                },
                {
                    default: () => (task.status ? TaskStatusTranslation[task.status as TaskStatus] : null) || enumToHuman(task.status),
                },
            ),
            h(
                NDropdown,
                {
                    options: dropdownOptions,
                    onSelect: handleDropdownSelect,
                },
                {
                    default: () =>
                        h(
                            NButton,
                            {
                                size: 'tiny',
                                quaternary: true,
                                style: { padding: '2px 4px' },
                            },
                            {
                                icon: () => h('i', { class: 'fa-solid fa-ellipsis-vertical', style: { fontSize: '12px' } }),
                            },
                        ),
                },
            ),
        ]);
    };

    return {
        editDrawerVisible,
        detailDrawerVisible,
        selectedTask,
        openEditDrawer,
        closeEditDrawer,
        handleSaveTask,
        openDetailDrawer,
        closeDetailDrawer,
        handleEditFromDetail,
        openStatusModalFromDetail,
        openStatusModal,
        openDateEditModal,
        assignTaskToMe,
        deleteTask,
        taskActionsMenu,
        taskStatusTag,
    };
};
